import { infiniteQueryOptions } from '@tanstack/react-query';

import { getTrainerClips } from '~/shared/api/generated/endpoints/trainer-clip/trainer-clip';
import type {
  TrainWithMeModulesClipsDtosResponsesTrainerClipClientResponse as ClientDto,
  TrainWithMeModulesClipsDtosResponsesTrainerClipTileResponse as TileDto,
} from '~/shared/api/generated/model';
import { isGone } from '~/shared/api/mutator';
import { civilDayInstant } from '~/shared/lib/calendarDate';

import type { CollectionClient, CollectionClip, CollectionPage } from '../model/clipCollection';

import { toClipAnchor, toClipPerson } from './clipQueries';

/** R27: thirty tiles a page, the backend's own default, stated so the page size is the app's. */
const PAGE_SIZE = 30;

function toClip(dto: TileDto): CollectionClip | null {
  if (dto.id === undefined || dto.customerId === undefined || dto.exerciseId === undefined) return null;
  return {
    id: dto.id,
    customerId: dto.customerId,
    exerciseId: dto.exerciseId,
    exerciseName: dto.exerciseName ?? '',
    performedOn: civilDayInstant(dto.performedOn ?? '') ?? 0,
    unread: dto.unread === true,
    author: toClipPerson(dto.author),
    anchor: toClipAnchor(dto.anchor),
    media: {
      posterUrl: dto.media?.posterUrl ?? null,
      durationSeconds: dto.media?.durationSeconds ?? null,
    },
  };
}

function toClient(dto: ClientDto): CollectionClient | null {
  if (dto.customerId === undefined) return null;
  return {
    customerId: dto.customerId,
    person: toClipPerson(dto.person),
    clipCount: dto.clipCount ?? 0,
    newestPerformedOn: civilDayInstant(dto.newestPerformedOn ?? '') ?? 0,
    hasUnread: dto.hasUnread === true,
  };
}

const present = <T>(value: T | null): value is T => value !== null;

/**
 * `GET /trainer/clips`, a page at a time from the cursor the previous page returned (R27). The
 * exercise names are content, so the key carries the content language they were read in.
 *
 * The posters are SIGNED urls, fresh on every answer, so a return to the window does not refetch
 * every page already read and swap every poster under the trainer; a visit after the 30s default
 * does, which is what brings a watched recording's mark off the wall.
 */
export function clipCollectionQueryOptions(customerId: number | null, lang: string) {
  return infiniteQueryOptions({
    queryKey: ['exercise-clip', 'collection', customerId, lang] as const,
    queryFn: async ({ pageParam, signal }): Promise<CollectionPage> => {
      const answer = await getTrainerClips(
        {
          limit: PAGE_SIZE,
          lang,
          ...(pageParam === null ? {} : { before: pageParam }),
          ...(customerId === null ? {} : { customerId }),
        },
        undefined,
        signal,
      );
      return {
        clips: (answer.clips ?? []).map(toClip).filter(present),
        clients: answer.clients?.map(toClient).filter(present) ?? null,
        nextCursor: answer.nextCursor ?? null,
      };
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchOnWindowFocus: false,
    // A client this trainer does not coach answers 404 (R27); asking again cannot change that.
    retry: (count, error) => !isGone(error) && count < 1,
  });
}
